/**
 * DistributionStatsDrawer — painel do admin para enxergar a distribuição
 * automática (round-robin / destino fixo) de contatos novos entre membros e
 * agentes. Agrega os eventos de atribuição já gravados (source="automation"),
 * sem store novo. Filtro por período + contagem por destino + recentes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, Loader2, Shuffle, Bot, User, AlertCircle } from 'lucide-react';
import assignmentService from '../../services/global/assignmentService';

const PERIODS = [
  { key: '7d', label: '7 dias', days: 7 },
  { key: '30d', label: '30 dias', days: 30 },
  { key: 'all', label: 'Tudo', days: null },
];

const DistributionStatsDrawer = ({ isOpen, onClose, colors }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const def = PERIODS.find((p) => p.key === period) || PERIODS[1];
    let since = null;
    if (def.days != null) {
      const d = new Date();
      d.setDate(d.getDate() - def.days);
      since = d.toISOString();
    }
    assignmentService.getDistributionStats(since)
      .then((res) => { if (!cancelled) setData((res && res.data) || res || null); })
      .catch((err) => {
        if (!cancelled) {
          console.error('[DistributionStats] load:', err);
          setError('Não foi possível carregar a distribuição.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, period]);

  const byTarget = data?.by_target || [];
  const maxCount = useMemo(
    () => byTarget.reduce((m, t) => Math.max(m, t.count || 0), 0) || 1,
    [byTarget]
  );

  if (!isOpen) return null;

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100,
        display: 'flex', justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560, maxWidth: '100%', height: '100%',
          backgroundColor: colors.bgPrimary,
          boxShadow: '-8px 0 32px rgba(0,0,0,0.25)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: `1px solid ${colors.borderColor}`,
          backgroundColor: colors.headerBg,
        }}>
          <div>
            <h3 style={{
              margin: 0, fontSize: 15, fontWeight: 600, color: colors.textPrimary,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Shuffle size={16} color={colors.accentPrimary} />
              Distribuição de contatos
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: colors.textSecondary }}>
              Quantos contatos cada membro/agente recebeu pela automação e pelas transferências do agente (roleta).
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: colors.textSecondary, padding: 6, borderRadius: 8,
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Period filter */}
        <div style={{
          display: 'flex', gap: 6, padding: '12px 18px',
          borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              style={{
                padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                cursor: 'pointer',
                border: `1px solid ${period === p.key ? colors.accentPrimary : colors.borderColor}`,
                backgroundColor: period === p.key ? `${colors.accentPrimary}15` : 'transparent',
                color: period === p.key ? colors.accentPrimary : colors.textSecondary,
              }}
            >
              {p.label}
            </button>
          ))}
          {data && (
            <span style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 12, color: colors.textTertiary }}>
              Total: <strong style={{ color: colors.textPrimary }}>{data.total || 0}</strong>
            </span>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: colors.textSecondary, fontSize: 13 }}>
              <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Carregando...
            </div>
          ) : error ? (
            <div style={{
              display: 'flex', gap: 8, alignItems: 'center', padding: 10,
              backgroundColor: '#DC262615', color: '#DC2626', borderRadius: 8, fontSize: 12,
            }}>
              <AlertCircle size={14} /> {error}
            </div>
          ) : byTarget.length === 0 ? (
            <p style={{ fontSize: 13, color: colors.textTertiary }}>
              Nenhuma distribuição automática registrada neste período.
            </p>
          ) : (
            <>
              {/* Por destino */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {byTarget.map((t) => (
                  <div key={`${t.type}:${t.id}`} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: `${colors.accentPrimary}18`, color: colors.accentPrimary,
                      }}>
                        {t.type === 'agent' ? <Bot size={12} /> : <User size={12} />}
                      </span>
                      <span style={{ fontSize: 13, color: colors.textPrimary, flex: 1, minWidth: 0 }}>
                        {t.name || t.id}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>{t.count}</span>
                    </div>
                    <div style={{ height: 8, borderRadius: 4, backgroundColor: colors.bgSecondary, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${Math.round((t.count / maxCount) * 100)}%`,
                        backgroundColor: t.type === 'agent' ? colors.accentPrimary : '#f59e0b',
                        borderRadius: 4,
                      }} />
                    </div>
                    {t.last_assigned_at && (
                      <span style={{ fontSize: 10.5, color: colors.textTertiary }}>
                        Último: {fmtDate(t.last_assigned_at)}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Recentes */}
              {(data?.recent || []).length > 0 && (
                <div style={{ marginTop: 22 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
                    Últimas distribuições
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {data.recent.slice(0, 30).map((ev, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '7px 0', borderBottom: `1px solid ${colors.borderColor}`,
                        fontSize: 12,
                      }}>
                        <span style={{ color: colors.textSecondary, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ev.phone}
                        </span>
                        <span style={{ color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: 4 }}>
                          {ev.assigned_to?.type === 'agent' ? <Bot size={11} /> : <User size={11} />}
                          {ev.assigned_to?.name || ev.assigned_to?.id}
                        </span>
                        <span style={{ color: colors.textTertiary, minWidth: 92, textAlign: 'right' }}>
                          {fmtDate(ev.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DistributionStatsDrawer;
